import React, { useCallback, useMemo } from 'react';
import { Col, Row } from 'reactstrap';
import { Form } from 'formik';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FormInputField from '../../common/components/formFields/FormInputField';
import CommonErrorLabel from '../../common/components/CommonErrorLabel';
import AddVehicleContentWrapper from '../AddVehicleContentWrapper';
import CommonButton from '../../common/components/CommonButton';
import SettingsSectionHeader from '../../settings/SettingsSectionHeader';
import '../../assets/css/componentStyles/AddNumberForm.css';
import InfinityLoader from '../../common/components/InfinityLoader';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';

function AddNumberForm({
  values,
  vehicleFound,
  form,
  onSearch,
  onNoAutoComplete,
  onTryAgain,
  isLoading,
  className,
  header = 'Registration Number',
  inputLabel = 'Registration number',
  searchButtonLabel = 'Search',
  notFoundMessage = "Unfortunately, we couldn't find any data for this vehicle. Would you like to try a different registration number?",
  manualButtonLabel = 'No, Fill Manually',
  tryAgainButtonLabel = 'Yes, Search Again',
  autofill,
  onValidate,
  validationErrors = {},
}) {
  const registrationPlateError = useMemo(
    () =>
      validationErrors?.VEHICLE_REGISTRATION?.['Vehicle Registration'] ||
      form.errors?.basicData?.registrationPlate,
    [validationErrors, form.errors],
  );
  const isFieldInvalid = !!registrationPlateError;
  const errorValue = registrationPlateError || '';
  const isSearchDisabled = useMemo(
    () => !!isLoading || !values?.basicData?.registrationPlate,
    [isLoading, values?.basicData?.registrationPlate],
  );

  const handleEnter = useCallback(
    (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      onSearch();
    },
    [onSearch],
  );

  const formClassName = classNames('text-left add-vehicle__add-number-form', className);

  return (
    <AddVehicleContentWrapper>
      <Form
        className={formClassName}
        onKeyDown={handleEnter}
        role="form"
        aria-label="Vehicle registration form"
        onBlur={onValidate}
      >
        <Row>
          <Col>
            <SettingsSectionHeader header={header} />
            <Row className="align-items-start text-left mt-4">
              <Col sm={12} md={3} className="mb-3 mb-md-0">
                <FormInputField
                  labelVariant="bordered"
                  inputClassName="add-number-form__input-field"
                  wrapperClassName="add-number-form__input-wrapper"
                  name="basicData.registrationPlate"
                  type="text"
                  label={inputLabel}
                  invalid={isFieldInvalid}
                  upperCase
                  required
                  aria-describedby="registration-error"
                />
                <CommonErrorLabel
                  value={errorValue}
                  id="registration-error"
                  role="alert"
                  aria-live="polite"
                />
              </Col>
              <Col sm={12} md={3}>
                <CommonButton
                  label={searchButtonLabel}
                  handleClick={onSearch}
                  disabled={isSearchDisabled}
                  className="w-100 mb-3"
                  aria-label={`${searchButtonLabel} for vehicle registration`}
                  variant={CommonButtonVariants.OUTLINED}
                />
              </Col>
            </Row>
            {isLoading && (
              <div className="add-number-form__loader-overlay">
                <InfinityLoader />
              </div>
            )}
            {vehicleFound === false && autofill === null && !isLoading && (
              <div role="alert" aria-live="polite">
                <div className="add-number-form__notification">
                  <div className="add-number-form__notification-icon" aria-hidden="true">
                    i
                  </div>
                  <p className="add-number-form__notification-text">{notFoundMessage}</p>
                </div>
                <div
                  className="add-number-form__actions"
                  role="group"
                  aria-label="Choose next action"
                >
                  <Col sm={12} md={3}>
                    <button
                      type="button"
                      className="add-number-form__action-button add-number-form__action-button--filled w-100"
                      onClick={onTryAgain}
                      aria-label="Try searching with a different registration number"
                    >
                      {tryAgainButtonLabel}
                    </button>
                  </Col>
                  <Col sm={12} md={3}>
                    <button
                      type="button"
                      className="add-number-form__action-button add-number-form__action-button--outlined w-100"
                      onClick={onNoAutoComplete}
                      aria-label="Choose to fill vehicle details manually"
                    >
                      {manualButtonLabel}
                    </button>
                  </Col>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Form>
    </AddVehicleContentWrapper>
  );
}

AddNumberForm.propTypes = {
  values: PropTypes.shape({
    basicData: PropTypes.shape({
      registrationPlate: PropTypes.string,
    }),
  }).isRequired,
  vehicleFound: PropTypes.bool,
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }).isRequired,
  onSearch: PropTypes.func.isRequired,
  onNoAutoComplete: PropTypes.func.isRequired,
  onTryAgain: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  header: PropTypes.string,
  inputLabel: PropTypes.string,
  searchButtonLabel: PropTypes.string,
  notFoundMessage: PropTypes.string,
  manualButtonLabel: PropTypes.string,
  tryAgainButtonLabel: PropTypes.string,
  onValidate: PropTypes.func,
};

AddNumberForm.defaultProps = {
  vehicleFound: null,
  isLoading: false,
  className: '',
  header: 'Registration Number',
  inputLabel: 'Vehicle registration plate',
  searchButtonLabel: 'Search',
  notFoundMessage:
    "Unfortunately, we couldn't find any data for this vehicle. Would you like to try a different registration number?",
  manualButtonLabel: 'No, Fill Manually',
  tryAgainButtonLabel: 'Yes, Search Again',
  onValidate: () => {},
};

export default AddNumberForm;
